import type { WorkspaceTeamMember } from '@/features/workspace/types';
import type { EvergreenChatModeOrigin, LiveChatMode } from '@/features/live/ablySessionChannels';
import type { ApiLanguage } from '@/i18n/languages';

export type WebinarLanguage = ApiLanguage;
export type WebinarStatus = 'PUBLISHED' | 'DRAFT';

// Statuts renvoyés par le suivi de duplication asynchrone (202 + long polling).
// INITIALIZATION = copie en cours, DRAFT = clone prêt, DUPLICATION_FAILED = échec côté back.
export type WebinarDuplicationStatus = 'INITIALIZATION' | 'DRAFT' | 'DUPLICATION_FAILED';

export interface WebinarDuplicationResult {
  webinarId: number;
  status: WebinarDuplicationStatus;
}

export interface WebinarPerson {
  fullname: string;
  job: string;
  pictureUrl?: string;
  userId?: string;
  accessCode?: string;
  accessUrl?: string;
}
export interface Webinar {
  registrationUrl?: string;
  replayRegistrationUrl?: string;
  nextSessionAt?: string | null;
  nextSessionTimezone?: string | null;
  lastSessionTimezone?: string | null;
  type?: 'LIVE' | 'EVERGREEN';
  id: number;
  title: string;
  shortDescription: string;
  description: string;
  language: WebinarLanguage;
  workspaceId: number;
  firstSessionDate?: string | null;
  lastSessionAt?: string | null;
  lastSessionId?: number;
  lastSessionIdentifier?: string;
  status?: WebinarStatus;
  presenters?: WebinarPerson[];
  speakers?: WebinarPerson[];
  moderators?: WebinarPerson[];
  participantsCount?: number;
  complete?: boolean;
  nextSessionId?: number;
  nextSessionIdentifier?: string;
  displayParticipantsMinThreshold?: number;
  bannedWords?: string[];
  chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
  displayParticipantsCountEnabled?: boolean;
  accessCodeEnabled?: boolean;
  transmissionMode?: 'NATIVE' | 'YOUTUBE';
  youtubeUrl?: string;
  // Alimentés uniquement par les endpoints agrégés (détail et liste des webinaires).
  registeredAttendeesCount?: number;
  freeRegistrationLimitReached?: boolean;
  // Evergreen publié dont la configuration courante peut être rejouée en salle de test.
  evergreenTestAvailable?: boolean;
}

export interface IndexItem {
  id: number;
  position: number;
  title: string;
  description: string;
}

export interface WebinarIndex {
  webinarId: number;
  workspaceId: number;
  items: IndexItem[];
  showIndex?: boolean;
}

export interface IndexItemInput {
  title: string;
  description: string;
  position?: number | null;
}

export interface IndexItemBulkReplaceInput {
  items: IndexItemInput[];
}

export type OfferConversionMode = 'EXTERNAL_LINK' | 'EMBED' | 'STRIPE_EMBEDDED_CHECKOUT';

// CONFIGURED : payable ; UNAVAILABLE : prix archivé ou compte Stripe déconnecté.
export type StripePaymentAvailability = 'NOT_APPLICABLE' | 'CONFIGURED' | 'UNAVAILABLE';

export type StripeRecurringInterval = 'day' | 'week' | 'month' | 'year';

export interface StripePriceOption {
  priceId: string;
  // En plus petite unité de la devise (centimes), comme Stripe. Absent sur les offres d'avant les
  // formules multiples, qui n'en proposent qu'une.
  unitAmount: number | null;
  currency: string | null;
  recurringInterval: StripeRecurringInterval | null;
  recurringIntervalCount: number | null;
  // Nombre d'échéances avant l'arrêt de l'abonnement ; null : paiement unique ou sans fin.
  installmentCount: number | null;
}

export interface StripePriceOptionInput {
  priceId: string;
  installmentCount: number | null;
}

export type OfferEmbedProvider =
  | 'CALENDLY'
  | 'ICLOSED'
  | 'TYPEFORM'
  | 'WHOP'
  | 'STRIPE_PRICING_TABLE'
  | 'KALLEO'
  | 'SYMETRIAL'
  | 'GOHIGHLEVEL';

export interface OfferTemplateContent {
  id: number;
  type: string;
  title: string;
  subTitle: string | null;
  description: string | null;
  buttonLabel: string;
  buttonLink: string | null;
  // Absent sur les offres d'avant l'embed : elles restent en lien externe.
  conversionMode?: OfferConversionMode;
  embedProvider?: OfferEmbedProvider | null;
  // Code collé par le client, renvoyé uniquement par les routes back-office.
  embedCode?: string | null;
  // Primitives extraites par le back, renvoyées par la route spectateur (et au back-office pour
  // Kalleo et Symetrial).
  embedUrl?: string | null;
  pricingTableId?: string | null;
  publishableKey?: string | null;
  formId?: string | null;
  region?: string | null;
  checkoutPlanId?: string | null;
  embedFallbackUrl?: string | null;
  // Paiement Stripe : renvoyés uniquement par les routes back-office.
  stripePriceId?: string | null;
  stripePaymentAvailability?: StripePaymentAvailability | null;
  // Formules proposées au spectateur, dans l'ordre d'affichage : la première est présélectionnée.
  stripePrices?: StripePriceOption[] | null;
  backgroundColor: string;
  borderColor: string;
  titleColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  imageUrl: string | null;
  imagePath: string | null;
  icon: string | null;
  iconColor: string | null;
  iconBackgroundColor: string | null;
}

export interface OfferTemplate {
  id: number;
  webinarId: number;
  workspaceId: number;
  privateTitle: string;
  abTesting: boolean;
  splitPercentA: number | null;
  thankYouPageOffer?: boolean;
  contents: OfferTemplateContent[];
}

export interface OfferTemplateInput {
  privateTitle: string;
  abTesting?: boolean | null;
  splitPercentA?: number | null;
  thankYouPageOffer?: boolean;
}

/** Agrégats admin pour une offre (GET .../offers/{offerId}/results, Ably `offer-results-update`) */
export interface OfferResults {
  webinarId?: number;
  offerId?: number;
  totalClicks: number;
  totalPurchases: number;
  // Clics/achats des spectateurs présents à l'instant T, base de calcul des pourcentages.
  presentClicks: number;
  presentPurchases: number;
  abTesting: boolean;
  clicksA: number;
  clicksB: number;
  purchasesA: number;
  purchasesB: number;
  assignmentsA: number;
  assignmentsB: number;
}
// Session Stripe Embedded Checkout créée par le back pour le spectateur, sur le compte du client.
export interface StripeEmbeddedCheckoutDto {
  clientSecret: string;
  stripeAccount: string;
}

export interface OfferLinkTestDto {
  url: string;
}

export function resolveOfferTestUrl(dto: OfferLinkTestDto | null | undefined): string {
  return dto?.url ?? '';
}

export interface OfferTemplateContentInput {
  title: string;
  subTitle?: string | null;
  description?: string | null;
  buttonLabel: string;
  buttonLink: string | null;
  conversionMode?: OfferConversionMode;
  embedCode?: string | null;
  embedFallbackUrl?: string | null;
  stripePriceId?: string | null;
  stripePrices?: StripePriceOptionInput[] | null;
  // Kalleo et Symetrial seulement : le back n'accepte pas de code pour ces fournisseurs.
  embedProvider?: OfferEmbedProvider | null;
  embedUrl?: string | null;
  backgroundColor: string;
  borderColor: string | null;
  titleColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  icon?: string | null;
  iconColor?: string | null;
  iconBackgroundColor?: string | null;
}

export type SurveyQuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'OPEN';

export interface SurveyAnswerOption {
  id: number;
  position: number;
  textContent: string;
}

export interface SurveyQuestion {
  id: number;
  textContent: string;
  type: SurveyQuestionType;
  position: number;
  required: boolean;
  maxSelections: number | null;
  randomizeOptions: boolean;
  options: SurveyAnswerOption[];
}

export interface SurveyTemplate {
  id: number;
  webinarId: number;
  workspaceId: number;
  title: string;
  subTitle?: string | null;
  borderColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  titleColor: string | null;
  questions: SurveyQuestion[];
  waitingRoomSurvey: boolean;
  displayWaitingRoomSurvey?: boolean;
}

export interface SurveyTemplateInput {
  title: string;
  subTitle?: string | null;
  borderColor?: string | null;
  accentColor?: string | null;
  backgroundColor?: string | null;
  titleColor?: string | null;
  waitingRoomSurvey: boolean;
  displayWaitingRoomSurvey?: boolean;
}

export interface SurveyQuestionInput {
  textContent: string;
  type: SurveyQuestionType;
  position?: number | null;
  required?: boolean | null;
  maxSelections?: number | null;
  randomizeOptions?: boolean | null;
}

export interface SurveyAnswerOptionInput {
  textContent: string;
  position?: number | null;
}

export interface PublicSurveySubmissionAnswer {
  questionId: number;
  optionIds: number[];
  freeText: string | null;
}

export interface PublicSurveySubmission {
  id: number;
  webinarId: number;
  templateId: number;
  sessionId: number;
  createdAt: string;
  answers: PublicSurveySubmissionAnswer[];
}

export interface SurveyOptionCount {
  optionId: number;
  optionText: string;
  count: number;
}

export interface SurveyQuestionResults {
  questionId: number;
  questionText: string;
  options: SurveyOptionCount[];
  freeTexts: string[];
}

export interface SurveyResults {
  webinarId: number;
  templateId: number;
  questions: SurveyQuestionResults[];
}

export interface QuizAnswer {
  questionId: number;
  optionId: number;
}

export interface QuizSubmission {
  id: number | null;
  webinarId: number;
  templateId: number;
  sessionId: number | null;
  subscriptionId: number;
  startedAt: string | null;
  submittedAt: string | null;
  answers: QuizAnswer[];
  score: number;
  totalPoints: number;
}

/** Option du quiz avec count (format API results) */
export interface QuizOptionCount {
  optionId: number;
  text?: string;
  correct?: boolean;
  count: number;
}

/** Question du quiz avec options et counts */
export interface QuizQuestionResults {
  questionId: number;
  text?: string;
  totalAnswers?: number;
  options: QuizOptionCount[];
}

/** Résultats agrégés du quiz (pourcentage et count par option, format API) */
export interface QuizResults {
  templateId: number;
  webinarId: number;
  workspaceId?: number;
  participants?: number;
  questions: QuizQuestionResults[];
}

export type WebinarResourceStatus = 'UPLOADED' | 'PROCESSING' | 'READY' | 'FAILED';

export interface WebinarResource {
  id: number;
  webinarId: number;
  workspaceId: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number;
  checksumSha256: string | null;
  position: number;
  status: WebinarResourceStatus;
  processingError: string | null;
  resourceUrl: string;
  resourceKey: string;
}

export interface WebinarResourceUploadInit {
  resourceKey: string;
  uploadUrl: string;
}

export interface ResourceUploadInitInput {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface WebinarResourceFinalizeInput {
  resourceKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount?: number | null;
  checksumSha256?: string | null;
  position?: number | null;
}

// ---------------------------------------------------------------------------
// Fichiers & Liens (SharedResource) : ressources fichier/lien diffusables en live.
// ---------------------------------------------------------------------------
export type SharedResourceType = 'FILE' | 'LINK';
export type SharedResourceStatus = 'PENDING_UPLOAD' | 'READY' | 'FAILED';

/** Représentation admin d'une ressource (config webinaire). */
export interface SharedResource {
  id: number;
  workspaceId: number;
  webinarId: number;
  type: SharedResourceType;
  status: SharedResourceStatus;
  title: string;
  description: string | null;
  position: number;
  objectKey: string | null;
  originalFileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  checksumSha256: string | null;
  externalUrl: string | null;
  uploadError: string | null;
}

export interface SharedResourcePage {
  content: SharedResource[];
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface SharedResourceFileUploadInitInput {
  title: string;
  description?: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256?: string | null;
  position?: number | null;
}

/** Réponse de POST .../shared-resources/files/upload-init : URL R2 présignée + headers requis. */
export interface SharedResourceUploadInit {
  sharedResourceId: number;
  objectKey: string;
  uploadUrl: string;
  expiresAt: string;
  requiredHeaders: Record<string, string>;
}

export interface SharedResourceLinkInput {
  title: string;
  description?: string | null;
  externalUrl: string;
  position?: number | null;
}

/** Représentation publique (attendee) d'une ressource diffusée. */
export interface SharedResourcePublic {
  id: number;
  type: SharedResourceType;
  title: string;
  description: string | null;
  originalFileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  externalUrl: string | null;
  live: boolean;
}

/** URL de téléchargement signée courte-durée pour un fichier. */
export interface SharedResourceDownloadUrl {
  downloadUrl: string;
  /** Absente quand le navigateur ne sait pas afficher le type de fichier. */
  previewUrl?: string | null;
  expiresAt: string;
}

export type WebinarSlideStatus = 'UPLOADED' | 'PROCESSING' | 'READY' | 'FAILED';

export interface WebinarSlide {
  id: number;
  webinarId: number;
  workspaceId: number;
  slideUrl: string;
  slideKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number;
  checksumSha256: string | null;
  position: number;
  status: WebinarSlideStatus;
  processingError: string | null;
}

export interface SlideUploadInit {
  uploadUrl: string;
  slideKey: string;
}

export interface SlideUploadInitInput {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface WebinarSlideFinalizeInput {
  slideKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount?: number | null;
  checksumSha256?: string | null;
  position?: number | null;
}

export interface WebinarSlideNote {
  pageNumber: number;
  text: string | null;
  lastEditedAt: string | null;
}

export interface SlideNotesBulkItem {
  pageNumber: number;
  text: string | null;
}

export interface SlideNotesBulkInput {
  notes: SlideNotesBulkItem[];
}

export interface SlideNoteUpdateInput {
  text?: string | null;
}

export type WebinarSubscriptionPageBackground = 'DEFAULT' | 'GRADIENT' | 'IMAGE';

export interface WebinarSubscriptionPage {
  webinarId: number;
  workspaceId: number;
  displayLogo: boolean;
  firstNameField: boolean;
  lastNameField: boolean;
  emailField: boolean;
  phoneField: boolean;
  firstNameMandatory: boolean;
  lastNameMandatory: boolean;
  emailMandatory: boolean;
  phoneMandatory: boolean;
  displaySpeakers: boolean;
  ctaButtonLabel: string | null;
  background: WebinarSubscriptionPageBackground;
  backgroundColor: string | null;
  backgroundGradient: string | null;
  backgroundImageUrl: string | null;
  backgroundImagePath: string | null;
  inputModified: boolean;
  backgroundModified: boolean;
  modified: boolean;
}

export interface WebinarSubscriptionPageInput {
  displayLogo: boolean;
  firstNameField: boolean;
  lastNameField: boolean;
  emailField: boolean;
  phoneField: boolean;
  firstNameMandatory: boolean;
  lastNameMandatory: boolean;
  emailMandatory: boolean;
  phoneMandatory: boolean;
  displaySpeakers: boolean;
  ctaButtonLabel?: string | null;
  background: WebinarSubscriptionPageBackground;
  backgroundColor: string | null;
  backgroundGradient: string | null;
  inputModified?: boolean;
  backgroundModified?: boolean;
}

export interface PublicWebinarSubscriptionForm {
  webinarId: number;
  displayLogo: boolean;
  displaySpeakers: boolean;
  ctaButtonLabel?: string | null;
  background: WebinarSubscriptionPageBackground;
  backgroundColor: string | null;
  backgroundGradient: string | null;
  backgroundImageUrl: string | null;
}

export interface WebinarSubscriptionDto {
  id: number;
  attendeeId: number;
  attendeeEmail: string;
  webinarSessionId: number;
  sessionIdentifier: string;
  token: string;
}

export type ReplayType = 'REPLICA' | 'EXTERNAL_LINK';

export type WebinarSessionType = 'LIVE' | 'EVERGREEN';
export type WebinarSessionRepetitionType = 'UNIQUE' | 'RECURRENT' | 'MULTIPLE';
export type WebinarSessionRecurrenceFrequencyType = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type WebinarSessionEndDateType = 'NONE' | 'DATE' | 'NUMBER_OF_SESSION';

export type WebinarSessionConfigurationSessionStatus = 'FINISHED' | 'UPCOMING' | string;

export interface WebinarSessionConfigurationSession {
  id: number;
  date: string;
  timezone?: string | null;
  status: WebinarSessionConfigurationSessionStatus;
  deletable: boolean;
}
export interface SessionScheduleInput {
  id?: number | null;
  date: string;
  timezone: string;
}

export interface EvergreenSession {
  webinarId: number;
  sessionId: number;
  sessionIdentifier: string;
}

export interface WebinarSessionConfiguration {
  type: WebinarSessionType;
  nextDates: string[] | null;
  sessions?: WebinarSessionConfigurationSession[];
  evergreenSession?: EvergreenSession | null;
}

export interface WebinarSessionConfigurationInput {
  type: WebinarSessionType;
  nextDates?: string[] | null;
  sessions?: SessionScheduleInput[] | null;
}

export interface PublicWebinarSession {
  session: {
    id: number;
    webinarId: number;
    workspaceId: number;
    webinarTitle: string;
    webinarDescription: string;
    webinarShortDescription: string;
    sessionType: WebinarSessionType;
    utcPlannedDate: string;
    accessCodeEnabled?: boolean;
    timezone: string;
    presenters: { fullname: string; job: string; pictureUrl?: string }[];
    speakers?: { fullname: string; job: string; pictureUrl?: string }[];
    moderators: string[];
    sourceConfigurationId: number;
    status: string;
    displayParticipantsMinThreshold?: number;
    displayParticipantsCountEnabled?: boolean;
    chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
    bannedWords?: string[];
    startedAt: string | null;
    finishedAt: string | null;
    replayEnabled: boolean;
    criticalBackupUrl?: string;
    transmissionMode?: 'NATIVE' | 'YOUTUBE';
    youtubeUrl?: string;
    youtubeActualStartTime?: string | null;
  };
  visualIdentity: {
    workspaceId: number;
    logoUrl: string;
    fontFamily: string;
    theme: string;
    accentColor: string;
    backgroundColor: string;
    secondaryBackgroundColor: string;
    contrastColor: string;
    gradient1Color1: string;
    gradient1Color2: string;
    gradient2Color1: string;
    gradient2Color2: string;
    modified: boolean;
  };
  broadcastAllowed: boolean;
  language?: WebinarLanguage;
}

export interface WebinarSession {
  accessCodeEnabled?: boolean;
  id: number;
  webinarId: number;
  workspaceId: number;
  identifier: string;
  webinarTitle: string;
  webinarDescription: string;
  webinarShortDescription: string;
  sessionType: WebinarSessionType;
  utcPlannedDate: string;
  timezone?: string | null;
  startedAt?: string | null;
  speakers: { fullname: string; job: string }[];
  moderators: string[];
  sourceConfigurationId: number;
  status: string;
}

/** Person with access URL (presenter, speaker, moderator in session detail) */
export interface SessionPersonWithAccess {
  fullname: string;
  job: string;
  pictureUrl?: string;
  accessUrl?: string;
  userId?: string;
}

/** Session detail from GET /workspaces/.../webinars/.../sessions/{sessionId} (with access URLs) */
export interface WebinarSessionDetail {
  id: number;
  identifier: string;
  webinarId: number;
  workspaceId: number;
  webinarTitle: string;
  webinarDescription?: string;
  webinarShortDescription?: string;
  sessionType: WebinarSessionType;
  utcPlannedDate: string;
  presenters: SessionPersonWithAccess[];
  speakers: SessionPersonWithAccess[];
  moderators: SessionPersonWithAccess[];
  sourceConfigurationId?: number;
  status: string;
  startedAt?: string | null;
  displayParticipantsMinThreshold?: number;
  timezone?: string;
  registrationUrl?: string;
  replayRegistrationUrl?: string;
}

export interface WebinarSessionAttendee {
  subscriptionId: number;
  attendeeId: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  registeredAt: string;
  // Liens live/replay propres à l'inscrit (ajoutés au DTO back, sous-tâche WELYA-212)
  liveLink?: string;
  replayLink?: string;
}

export interface SessionAttendeesImportResult {
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;
  duplicateRowsCount: number;
}

export type SessionAttendeesImportJobStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export interface SessionAttendeesImportStatus {
  importId: number;
  status: SessionAttendeesImportJobStatus;
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;
  duplicateRowsCount: number;
  errorCode: string | null;
}

export interface PublicWebinarMe {
  id: number;
  webinarId: number;
  workspaceId: number;
  sessionId: number;
  fullName: string;
  firstName: string;
  email: string;
  status: string;
}

export interface ReplayConfiguration {
  webinarId: number;
  workspaceId: number;
  active: boolean;
  noLimitDate: boolean;
  limitDate: string | null;
  validityDays: number;
  replayType: ReplayType | null;
  externalLink: string | null;
  displayVideoCommands: boolean;
  allowComments: boolean;
  typeModified?: boolean;
  accessModified?: boolean;
  validityModified?: boolean;
}

export interface ReplayConfigurationInput {
  active: boolean;
  noLimitDate: boolean;
  limitDate: string | null;
  validityDays: number;
  replayType: ReplayType | null;
  externalLink: string | null;
  displayVideoCommands: boolean;
  allowComments: boolean;
  typeModified?: boolean;
  accessModified?: boolean;
  validityModified?: boolean;
}

export interface PastSession {
  id: number;
  identifier: string;
  plannedDate: string;
  startedAt?: string;
  transmissionMode: 'NATIVE' | 'YOUTUBE';
  requiresImportedMp4: boolean;
}

export interface WebinarForEvergreen {
  id: number;
  title: string;
  createdDate: string;
  finishedSessions: PastSession[];
}

export type EvergreenChatMode = 'LIVE_ACTIVE' | 'DISABLED';

export interface EvergreenChatConfiguration {
  chatMode: EvergreenChatMode;
  publicChatVisible: boolean;
  recordAttendeeMessages: boolean;
  messages: EvergreenTimelineEvent[];
}

export interface UpdateEvergreenChatConfigInput {
  chatMode?: EvergreenChatMode;
  publicChatVisible?: boolean;
  recordAttendeeMessages?: boolean;
}

export type EvergreenChatAuthorRole = 'PRESENTER' | 'MODERATOR' | 'SPEAKER' | 'ATTENDEE';

export type EvergreenManualAttendeeLabel = Extract<
  ParticipationStatusCriteria,
  'TOP_25_PERCENT' | 'TOP_50_PERCENT' | 'TOP_75_PERCENT' | 'TOP_100_PERCENT' | 'CONVERSION'
>;

export interface AddManualChatMessageInput {
  hours: number;
  minutes: number;
  seconds: number;
  authorName: string;
  authorRole: EvergreenChatAuthorRole;
  content: string;
  statusCriteria: EvergreenManualAttendeeLabel | null;
}

export interface EvergreenConfiguration {
  webinarId: number;
  autoplayEnabled: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number;
  leavePopupEnabled: boolean;
  leavePopupTitle: string;
  videoResourceId: number | null;
  videoUrl: string | null;
  videoName: string | null;
  durationSeconds: number | null;
  ready: boolean;
  awaitingImportedMp4: boolean;
  conversionTotalItems: number;
  conversionProcessedItems: number;
  interactionsOutsideVideo: EvergreenInteractionOutsideVideo[];
  pendingImportedMp4FileName: string | null;
  /** Live dont la vidéo est diffusée, null pour une vidéo MP4. */
  videoSourceSession: EvergreenSourceSession | null;
  /** Live dont les interactions sont reprises, null pour « Partir de zéro ». */
  interactionSourceSession: EvergreenSourceSession | null;
}

export interface EvergreenSourceSession {
  id: number;
  identifier: string;
}

export type EvergreenVideoSourceInput =
  | { videoSourceType: 'LIVE'; videoSourceSessionId: number }
  | { videoSourceType: 'MP4'; videoResourceId: number };

/** Absente du payload, la source d'interactions en place est conservée. */
export type EvergreenInteractionSourceInput =
  | { interactionSourceType: 'LIVE'; interactionSourceSessionId: number }
  | { interactionSourceType: 'NONE' };

export type EvergreenSourcesInput = EvergreenVideoSourceInput &
  (EvergreenInteractionSourceInput | Record<never, never>);

export interface EvergreenInteractionOutsideVideo {
  eventId: number;
  eventType: 'OFFER' | 'SURVEY' | 'QUIZ' | 'RESOURCE_SHOW' | 'PURCHASE_NOTIFICATION';
  startOffsetSeconds: number;
  endOffsetSeconds: number | null;
}

export interface EvergreenConfigurationInput {
  autoplayEnabled: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number;
  leavePopupEnabled: boolean;
  leavePopupTitle: string;
  videoResourceId: number | null;
  videoUrl: string | null;
  videoName: string | null;
}

export type EvergreenTimelineEventType =
  | 'MEDIA'
  | 'SLIDES'
  | 'OFFER'
  | 'SURVEY'
  | 'QUIZ'
  | 'EVALUATION'
  | 'SHARED_RESOURCE'
  | 'RESOURCE_SHOW'
  | 'RESOURCE_HIDE'
  | 'SUPPORT_MESSAGE'
  | 'PURCHASE_NOTIFICATION'
  | 'SESSION_FINISH'
  | 'CHAT_REACTION'
  | 'MESSAGE_PIN'
  | 'MESSAGE_UNPIN'
  | 'LIVE_REACTION'
  | 'CHAT_MODE_CHANGED';
export type EvergreenTimelineResourceType =
  | 'WEBINAR_RESOURCE'
  | 'WEBINAR_RECORDING'
  | 'OFFER_TEMPLATE'
  | 'SURVEY_TEMPLATE'
  | 'QUIZ_TEMPLATE'
  | 'EVALUATION_TEMPLATE'
  | 'SHARED_RESOURCE'
  | 'INLINE';

export type EvergreenTimelineEventOrigin =
  'LIVE_CONVERSION' | 'AUTOMATED_LEGACY' | 'MANUAL' | 'CSV';

export interface EvergreenTimelineEvent {
  id: number;
  eventType: EvergreenTimelineEventType;
  resourceType: EvergreenTimelineResourceType;
  origin?: EvergreenTimelineEventOrigin | null;
  resourceId: number | null;
  startOffsetSeconds: number;
  endOffsetSeconds: number | null;
  title: string | null;
  payloadJson: string | null;
  metadata: string | null;
  displayOrder: number;
}

export interface EvergreenTimelineEventInput {
  id?: number | null;
  eventType: EvergreenTimelineEventType;
  resourceType: EvergreenTimelineResourceType;
  resourceId: number | null;
  startOffsetSeconds?: number | null;
  endOffsetSeconds?: number | null;
  title?: string | null;
  payloadJson?: string | null;
  metadata?: string | null;
  displayOrder?: number | null;
}

export interface PublicEvergreenChatConfiguration {
  chatMode: EvergreenChatMode;
  publicChatVisible: boolean;
  /** Mode du live source rejoué au timecode courant. */
  attendeeChatMode?: LiveChatMode;
  attendeeChatModeOrigin?: EvergreenChatModeOrigin;
  attendeeChatModeRevision?: number;
}

/** Salle de test Evergreen : rejoue la configuration courante sans rien enregistrer. */
export interface EvergreenTestRun {
  id: string;
  // Instants UTC sans fuseau, comme les autres dates du back.
  startedAt: string;
  expiresAt: string;
}

export interface EvergreenTestTimelineEvent {
  event: EvergreenTimelineEvent;
  // Offsets réellement appliqués par une vraie session (compensation et millisecondes comprises).
  effectiveStartOffsetMillis: number;
  effectiveEndOffsetMillis: number | null;
}

export interface EvergreenTestSharedResource {
  id: number;
  type: SharedResourceType;
  status: SharedResourceStatus;
  title: string;
  description: string | null;
  position: number;
  originalFileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  externalUrl: string | null;
}

export interface EvergreenTestViewer {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
}

export interface EvergreenTestSnapshot {
  webinarId: number;
  videoUrl: string;
  fallbackVideoUrl: string | null;
  durationSeconds: number;
  autoplayEnabled: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number | null;
  leavePopupEnabled: boolean;
  leavePopupTitle: string | null;
  chatMode: EvergreenChatMode;
  publicChatVisible: boolean;
  recordAttendeeMessages: boolean;
  attendeeChatMode: LiveChatMode;
  events: EvergreenTestTimelineEvent[];
  // Liens laissés avec leurs {placeholders} : le front les remplit avec `viewer`.
  offers: OfferTemplate[];
  surveys: SurveyTemplate[];
  quizzes: QuizTemplate[];
  sharedResources: EvergreenTestSharedResource[];
  quitPage: QuitPage | null;
  viewer: EvergreenTestViewer;
}

export interface EvergreenTestRuntime {
  testRunId: string;
  startedAt: string;
  expiresAt: string;
  chatDisabledMessage: string;
  snapshot: EvergreenTestSnapshot;
}

export type EvergreenScheduledInteractionType = 'OFFER' | 'SURVEY' | 'QUIZ' | 'SHARED_RESOURCE';

export interface EvergreenScheduledInteractionInput {
  type: EvergreenScheduledInteractionType;
  resourceId: number;
  startOffsetSeconds: number;
  endOffsetSeconds: number;
}

export interface EvergreenManualPurchaseNotificationInput {
  message: string;
  startOffsetSeconds: number;
  /** Le back recopie l'icône et les couleurs de cette offre ; null = thème du webinaire. */
  offerTemplateId: number | null;
}

export interface EvergreenScheduledInteraction extends EvergreenScheduledInteractionInput {
  id: number;
}

export interface EvergreenVideoCut {
  startSeconds: number;
  durationSeconds: number;
}

/** Nature d'un MP4 qui remplace la vidéo d'un Evergreen existant. */
export type EvergreenMp4ReplacementType =
  'INDEPENDENT_VIDEO' | 'PREVIOUS_VIDEO_UNCUT' | 'PREVIOUS_VIDEO_WITH_CUTS';

export interface EvergreenMp4UploadInitInput {
  fileName: string;
  mimeType: 'video/mp4';
  sizeBytes: number;
  /** Passages retirés, appliqués à toute la timeline à l'enregistrement de la vidéo. */
  cuts?: EvergreenVideoCut[];
  /** Absent au premier import : seul un remplacement déclare sa nature. */
  replacementType?: EvergreenMp4ReplacementType;
}

export interface EvergreenMp4PartUploadUrl {
  partNumber: number;
  uploadUrl: string;
}

export interface EvergreenMp4UploadInit {
  uploadId: number;
  objectKey: string;
  multipartUploadId: string;
  partSizeBytes: number;
  parts: EvergreenMp4PartUploadUrl[];
}

export interface EvergreenMp4UploadPartsRequest {
  firstPartNumber: number;
  partsCount: number;
}

export interface EvergreenMp4UploadCompleteInput {
  parts: { partNumber: number; eTag: string }[];
}

export type EvergreenMp4UploadStatus =
  'INITIATED' | 'VALIDATING' | 'PENDING_PROMOTION' | 'READY' | 'FAILED' | 'ABORTED';

export type EvergreenMp4FailureCode =
  | 'NOT_MP4'
  | 'UNSUPPORTED_CODEC'
  | 'DURATION_EXCEEDED'
  | 'SIZE_MISMATCH'
  | 'DOWNLOAD_FAILED'
  | 'PROBE_FAILED'
  | 'INTERNAL_ERROR'
  | 'SOURCE_SESSION_CHANGED'
  | 'INTERACTION_OUTSIDE_VIDEO_DURATION'
  | 'EVERGREEN_SESSION_IN_PROGRESS'
  | 'CUTS_DURATION_MISMATCH';

export interface EvergreenMp4UploadStatusDto {
  uploadId: number;
  status: EvergreenMp4UploadStatus;
  durationSeconds: number | null;
  /** Vidéo validée à enregistrer comme source, null tant que la validation n'a pas abouti. */
  videoResourceId: number | null;
  failureCode: EvergreenMp4FailureCode | null;
}

export type WaitingRoomPageBackground = 'DEFAULT' | 'GRADIENT' | 'IMAGE';

export type WaitingRoomInteractionType = 'NONE' | 'QUIZ' | 'SURVEY';

export interface WaitingRoomPage {
  displayLogo: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number;
  waitingRoomCount: number;
  webinarId: number;
  workspaceId: number;
  displaySpeakers: boolean;
  displayGuests: boolean;
  displayIndex: boolean;
  background: WaitingRoomPageBackground;
  backgroundColor: string | null;
  backgroundGradient: string | null;
  backgroundImageUrl: string | null;
  backgroundImagePath: string | null;
  mainImage: string | null;
  mainImageUrl: string | null;
  mainVideo: string | null;
  mainAssetType: 'IMAGE' | 'VIDEO';
  mainVideoUrl: string | null;
  displayMainMedia: boolean;
  modified: boolean;
  interactionType: WaitingRoomInteractionType;
  backgroundModified?: boolean;
}

export interface WaitingRoomPageInput {
  displayLogo: boolean;
  displaySpeakers: boolean;
  displayGuests: boolean;
  displayIndex: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number;
  background: WaitingRoomPageBackground;
  backgroundColor: string | null;
  backgroundGradient: string | null;
  interactionType: WaitingRoomInteractionType;
  backgroundModified?: boolean;
  mainAssetType?: 'IMAGE' | 'VIDEO';
  displayMainMedia?: boolean;
}

export type ThankYouPageOfferConfigurationMode = 'NEW_OFFER' | 'EXISTING_OFFER';

export interface ThankYouPage {
  offerId?: number | null;
  offerConfigurationMode?: ThankYouPageOfferConfigurationMode | null;
  displayLogo: boolean;
  webinarId: number;
  workspaceId: number;
  displaySocialMedia: boolean;
  displaySpeakerProfile: boolean;
  displayOffer: boolean;
  displayImage: boolean;
  displayEvaluation: boolean;
  imageUrl: string | null;
  imagePath: string | null;
  modified: boolean;
  presenterPictureUrl?: string | null;
  presenterName?: string | null;
  title?: string | null;
  subtitle?: string | null;
  evaluationIntroduction?: string | null;
}

export interface ThankYouPageInput {
  displayLogo: boolean;
  displaySocialMedia: boolean;
  displaySpeakerProfile: boolean;
  displayOffer: boolean;
  displayImage: boolean;
  displayEvaluation: boolean;
  offerId?: number | null;
  offerConfigurationMode?: ThankYouPageOfferConfigurationMode | null;
  title?: string | null;
  subtitle?: string | null;
  evaluationIntroduction?: string | null;
}

export type QuitPageType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE';

export interface QuitPageReason {
  id: number;
  reason: string;
  explanationText: string;
  imageUrl: string | null;
  imageAlt: string | null;
  position: number;
  displayQrCode?: boolean;
}

export interface QuitPage {
  webinarId: number;
  workspaceId: number;
  title: string;
  questionText: string;
  type: QuitPageType;
  otherReason: boolean;
  reasons: QuitPageReason[];
  showPopup: boolean;
  backgroundColor?: string | null;
  textColor?: string | null;
  buttonColor?: string | null;
  buttonTextColor?: string | null;
}

export interface QuitPageInput {
  title: string;
  questionText: string;
  type: QuitPageType;
  otherReason: boolean;
  showPopup: boolean;
  backgroundColor?: string | null;
  textColor?: string | null;
  buttonColor?: string | null;
  buttonTextColor?: string | null;
}

export interface QuitPageReasonInput {
  reason: string;
  explanationText: string;
  position?: number | null;
  imageAlt?: string | null;
  displayQrCode?: boolean;
}

export type QuizQuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE';

export interface QuizAnswerOption {
  id: number;
  position: number;
  textContent: string;
  correct: boolean;
}

export interface QuizQuestion {
  id: number;
  textContent: string;
  type: QuizQuestionType;
  position: number;
  points: number;
  timeLimitSeconds: number | null;
  options: QuizAnswerOption[];
}

export interface QuizTemplate {
  id: number;
  webinarId: number;
  workspaceId: number;
  name: string;
  subTitle: string | null;
  enabledDuringLive: boolean;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  passingScore: number | null;
  visibleFrom: string | null;
  visibleUntil: string | null;
  published: boolean;
  questions: QuizQuestion[];
  borderColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  titleColor: string | null;
  waitingRoomQuiz: boolean;
  displayWaitingRoomQuiz: boolean;
}

export interface QuizTemplateInput {
  name: string;
  subTitle?: string | null;
  enabledDuringLive: boolean;
  shuffleQuestions?: boolean | null;
  shuffleOptions?: boolean | null;
  passingScore?: number | null;
  visibleFrom?: string | null;
  visibleUntil?: string | null;
  published?: boolean | null;
  borderColor?: string | null;
  accentColor?: string | null;
  backgroundColor?: string | null;
  titleColor?: string | null;
  waitingRoomQuiz?: boolean | null;
  displayWaitingRoomQuiz?: boolean | null;
}

export interface QuizQuestionInput {
  textContent: string;
  type: QuizQuestionType;
  position?: number | null;
  points?: number | null;
  timeLimitSeconds?: number | null;
}

export interface QuizAnswerOptionInput {
  textContent: string;
  correct?: boolean | null;
  position?: number | null;
}

export type EvaluationQuestionType =
  'SMILEYS' | 'THUMBS' | 'STARS' | 'TEXT_AREA' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE';

// Mode du sondage de fin : notation classique ou question à choix multiples.
export type EvaluationMode = 'RATING' | 'MULTIPLE_CHOICE';

export interface EvaluationAnswerOption {
  id?: number;
  position: number;
  textContent: string;
}

export interface EvaluationQuestion {
  id: number;
  text: string;
  type: EvaluationQuestionType;
  position: number;
  // Présentes uniquement pour les questions de type SINGLE_CHOICE / MULTIPLE_CHOICE.
  options?: EvaluationAnswerOption[];
}

export interface EvaluationTemplate {
  id: number;
  webinarId: number;
  title: string;
  mode: EvaluationMode;
  questions: EvaluationQuestion[];
}

export interface EvaluationAnswerOptionInput {
  textContent: string;
  position?: number | null;
}

export interface EvaluationQuestionInput {
  text: string;
  type: EvaluationQuestionType;
  position?: number | null;
  // Le back fait un clear + recreate des options à partir de cette liste.
  options?: EvaluationAnswerOptionInput[];
}

export interface EvaluationTemplateInput {
  title: string;
  mode?: EvaluationMode;
}

export interface EvaluationSubmissionInput {
  sessionId: number;
  answers: Array<{
    questionId: number;
    ratingValue?: number | null;
    freeText?: string | null;
    // Identifiants des options choisies pour les questions à choix.
    optionIds?: number[];
  }>;
}

export type SpeakerType = 'TEAM_MEMBER' | 'GUEST';

export interface Speaker {
  id: number;
  speakerId: number;
  workspaceId: number;
  webinarId: number;
  identity: string;
  displayName: string;
  jobTitle: string;
  type: SpeakerType;
  userId?: string | null;
  guestFullName?: string | null;
  guestEmail?: string | null;
  guestPictureUrl?: string | null;
  temporaryGuestEmail?: string | null;
}

export interface SpeakerInput {
  overrideFullName: string;
  jobTitle: string;
  userId?: string | null;
  guestFullName?: string | null;
  guestEmail?: string | null;
}

export interface Moderator {
  id: number;
  workspaceId: number;
  webinarId: number;
  overrideFullName: string | null;
  displayName: string;
  jobTitle: string;
  userId: string;
}

export interface ModeratorInput {
  overrideFullName?: string;
  jobTitle: string;
  userId: string;
}

export interface Presenter {
  id: number;
  workspaceId: number;
  webinarId: number;
  speakerId?: number;
  displayName: string;
  overrideFullName: string | null;
  jobTitle: string | null;
  userId: string;
  userFirstName: string | null;
  userLastName: string | null;
  userPictureUrl: string | null;
}

export interface PresenterInput {
  overrideFullName?: string | null;
  jobTitle?: string | null;
  userId: string;
}

export type SessionRole = 'SPEAKER' | 'MODERATOR';

export interface SessionInvite {
  id: number;
  workspaceId: number;
  webinarId: number;
  email: string;
  role: SessionRole | null;
  expiresAt: string | null;
  revoked: boolean;
}

export interface TechnicalAuthUser {
  apiKeyInternalId: number;
  apiKeyId: string;
  workspaceId: number;
  name: string;
}

export interface IntegrationApiKeyDto {
  keyId: string;
  name: string;
  prefix: string;
  status: string;
  createdAt: string;
  lastUsedAt: string | null;
  rotatedAt: string | null;
}

export interface CreateIntegrationApiKeyInput {
  name: string;
}

export interface IntegrationApiKeyCreatedDto {
  keyId: string;
  name: string;
  prefix: string;
  createdAt: string;
  apiKey: string;
}

export type WebhookEventType = 'ATTENDEE_CONNECTION' | 'ATTENDEE_END_STATS' | 'ATTENDEE_REGISTERED';

export interface WebhookDto {
  id: number;
  webinarId: number;
  eventType: WebhookEventType;
  url: string;
  secret?: string | null;
  hasSecret?: boolean;
  active: boolean;
  createdDate: string;
}

export interface WebhookInput {
  url: string;
  eventType: WebhookEventType;
  secret?: string | null;
  active?: boolean | null;
}

export type WebinarIntegrationProviderType = 'ACTIVE_CAMPAIGN' | 'HUBSPOT';

export type WebinarAutomationTriggerEventType =
  | 'JOINED'
  | 'LEFT'
  | 'HEARTBEAT'
  | 'CHAT_MESSAGE'
  | 'CHAT_REACTION'
  | 'MESSAGE_PIN'
  | 'MESSAGE_UNPIN'
  | 'SURVEY_SUBMISSION'
  | 'EVALUATION_SUBMISSION'
  | 'QUIZ_SUBMISSION'
  | 'OFFER_SEEN'
  | 'OFFER_CLICK'
  | 'LIVE_REACTION'
  | 'OFFER_PURCHASE'
  | 'RESOURCE_SHOW'
  | 'RESOURCE_HIDE'
  | 'PDF_PAGE_CHANGE'
  | 'VIDEO_SEEK'
  | 'VIDEO_PLAY'
  | 'VIDEO_PAUSE'
  | 'VIDEO_UNMUTE'
  | 'VIDEO_MUTE'
  | 'HIDE_SURVEY_RESPONSE'
  | 'SHOW_SURVEY_RESPONSE'
  | 'PURCHASE_NOTIFICATION'
  | 'MESSAGE_PIN'
  | 'MESSAGE_UNPIN'
  | 'MESSAGE_REPLY'
  | 'WAITING_ROOM_HEARTBEAT';

export interface WebinarIntegrationTrigger {
  id: number;
  providerType: WebinarIntegrationProviderType;
  triggerEventType: WebinarAutomationTriggerEventType;
  actionType: string;
  settingsJson: string;
  enabled: boolean;
}

export interface WebinarIntegrationTriggerInput {
  providerType: WebinarIntegrationProviderType;
  triggerEventType: WebinarAutomationTriggerEventType;
  actionType: string;
  settingsJson: string;
  enabled?: boolean | null;
}

export type ParticipationStatusCriteria =
  | 'PRESENTER'
  | 'MODERATOR'
  | 'FIRST_SCORE'
  | 'SECOND_SCORE'
  | 'THIRD_SCORE'
  | 'TOP_25_PERCENT'
  | 'TOP_50_PERCENT'
  | 'TOP_75_PERCENT'
  | 'TOP_100_PERCENT'
  | 'CONVERSION';

export interface ParticipationStatusDto {
  id: number;
  criteria: ParticipationStatusCriteria;
  label: string;
  color: string;
  icon: string;
}

export interface ParticipationStatusInput {
  criteria: ParticipationStatusCriteria;
  label: string;
  color: string;
  icon: string;
}

export interface ParticipationStatusBulkInput {
  showStatus?: boolean;
  statuses: ParticipationStatusInput[];
}

export interface WebinarParticipationStatusConfigDto {
  showStatus: boolean;
  statuses: ParticipationStatusDto[];
}

export interface WebinarState {
  webinars: Webinar[];
  webinarsTotalElements: number;
  webinarsTotalPages: number;
  loading: boolean;
  error: string | null;
  createWebinarInitialLoading: boolean;
  currentWebinar: Webinar | null;
  currentWebinarIndex: WebinarIndex | null;
  indexLoading: boolean;
  indexError: string | null;
  currentWebinarSubscriptionPage: WebinarSubscriptionPage | null;
  subscriptionPageLoading: boolean;
  subscriptionPageError: string | null;
  currentReplayConfiguration: ReplayConfiguration | null;
  replayConfigurationLoading: boolean;
  replayConfigurationError: string | null;
  currentEvergreenConfiguration: EvergreenConfiguration | null;
  evergreenConfigurationLoading: boolean;
  evergreenConfigurationError: string | null;
  evergreenTimeline: EvergreenTimelineEvent[];
  evergreenTimelineLoading: boolean;
  evergreenTimelineError: string | null;
  availableWebinars?: WebinarForEvergreen[];
  availableWebinarsTotalElements?: number;
  availableWebinarsTotalPages?: number;
  availableWebinarsLoading?: boolean;
  availableWebinarsError?: string | null;
  evergreenConvertLoading?: boolean;
  evergreenConvertError?: string | null;
  evergreenChatImportLoading?: boolean;
  evergreenChatImportError?: string | null;
  currentEvergreenChatConfiguration: EvergreenChatConfiguration | null;
  evergreenChatConfigurationLoading: boolean;
  evergreenChatConfigurationError: string | null;
  currentSessionConfiguration: WebinarSessionConfiguration | null;
  sessionConfigurationWebinarId: number | null;
  sessionConfigurationLoading: boolean;
  sessionConfigurationError: string | null;
  webinarSessions?: WebinarSession[];
  webinarSessionsLoading?: boolean;
  webinarSessionsError?: string | null;
  currentWebinarSession?: WebinarSession | null;
  currentWebinarSessionLoading?: boolean;
  currentWebinarSessionError?: string | null;
  /** Session details with access URLs, keyed by sessionId (for program table) */
  sessionDetailsBySessionId?: Record<number, WebinarSessionDetail>;
  sessionDetailsLoading?: boolean;
  sessionDetailsError?: string | null;
  sessionChatExportLoading?: boolean;
  sessionChatExportError?: string | null;
  sessionAttendees?: WebinarSessionAttendee[];
  sessionAttendeesTotalElements?: number;
  sessionAttendeesTotalPages?: number;
  sessionAttendeesLoading?: boolean;
  sessionAttendeesError?: string | null;
  sessionAttendeesExport: {
    loading: boolean;
    error: string | null;
  };
  sessionAttendeesImport: {
    loading: boolean;
    error: string | null;
    result: SessionAttendeesImportResult | null;
  };
  currentWaitingRoomPage: WaitingRoomPage | null;
  waitingRoomPageLoading: boolean;
  waitingRoomPageError: string | null;
  currentThankYouPage: ThankYouPage | null;
  thankYouPageLoading: boolean;
  thankYouPageError: string | null;
  offerTemplates: OfferTemplate[];
  offerTemplatesLoading: boolean;
  offerTemplatesError: string | null;
  currentOfferTemplate: OfferTemplate | null;
  currentOfferTemplateLoading: boolean;
  currentOfferTemplateError: string | null;
  surveyTemplates: SurveyTemplate[];
  surveyTemplatesLoading: boolean;
  surveyTemplatesError: string | null;
  currentSurveyTemplate: SurveyTemplate | null;
  currentSurveyTemplateLoading: boolean;
  currentSurveyTemplateError: string | null;
  surveyQuestions: SurveyQuestion[];
  surveyQuestionsLoading: boolean;
  surveyQuestionsError: string | null;
  surveyAnswerOptions: SurveyAnswerOption[];
  surveyAnswerOptionsLoading: boolean;
  surveyAnswerOptionsError: string | null;
  currentQuitPage: QuitPage | null;
  quitPageLoading: boolean;
  quitPageError: string | null;
  webinarResources: WebinarResource[];
  webinarResourcesTotalElements: number;
  webinarResourcesTotalPages: number;
  webinarResourcesLoading: boolean;
  webinarResourcesError: string | null;
  webinarResourceUploadInit: WebinarResourceUploadInit | null;
  webinarResourceUploadInitLoading: boolean;
  webinarResourceUploadInitError: string | null;
  webinarResourceFinalizeLoading: boolean;
  webinarResourceFinalizeError: string | null;
  // Fichiers & Liens (config) : liste paginée (page courante uniquement, size 20).
  sharedResources: SharedResource[];
  sharedResourcesLoading: boolean;
  sharedResourcesError: string | null;
  sharedResourcesPage: number;
  sharedResourcesTotalPages: number;
  sharedResourcesTotalElements: number;
  // Fichiers & Liens (live) : ressources actuellement diffusées pour la session en cours.
  currentSharedResources: SharedResourcePublic[];
  webinarSlides: WebinarSlide[];
  webinarSlidesLoading: boolean;
  webinarSlidesError: string | null;
  currentWebinarSlide: WebinarSlide | null;
  currentWebinarSlideLoading: boolean;
  currentWebinarSlideError: string | null;
  webinarSlideUploadInit: SlideUploadInit | null;
  webinarSlideUploadInitLoading: boolean;
  webinarSlideUploadInitError: string | null;
  webinarSlideFinalizeLoading: boolean;
  webinarSlideFinalizeError: string | null;
  webinarSlideNotes: WebinarSlideNote[];
  webinarSlideNotesLoading: boolean;
  webinarSlideNotesError: string | null;
  webinarSlideNotesSaving: boolean;
  webinarSlideNotesSavingError: string | null;
  webinarSlideNoteUpdating: boolean;
  webinarSlideNoteUpdatingError: string | null;
  quizTemplates: QuizTemplate[];
  quizTemplatesLoading: boolean;
  quizTemplatesError: string | null;
  currentQuizTemplate: QuizTemplate | null;
  currentQuizTemplateLoading: boolean;
  currentQuizTemplateError: string | null;
  quizQuestions: QuizQuestion[];
  quizQuestionsLoading: boolean;
  quizQuestionsError: string | null;
  quizAnswerOptions: QuizAnswerOption[];
  quizAnswerOptionsLoading: boolean;
  quizAnswerOptionsError: string | null;
  speakers: Speaker[];
  speakersLoading: boolean;
  speakersError: string | null;
  currentSpeaker: Speaker | null;
  currentSpeakerLoading: boolean;
  currentSpeakerError: string | null;
  moderators: Moderator[];
  moderatorsLoading: boolean;
  moderatorsError: string | null;
  currentModerator: Moderator | null;
  currentModeratorLoading: boolean;
  currentModeratorError: string | null;
  presenters: Presenter[];
  presentersLoading: boolean;
  presentersError: string | null;
  currentPresenter: Presenter | null;
  currentPresenterLoading: boolean;
  currentPresenterError: string | null;
  sessionInvites: SessionInvite[];
  sessionInvitesLoading: boolean;
  sessionInvitesError: string | null;
  participationStatuses: ParticipationStatusDto[];
  showParticipationStatus: boolean;
  participationStatusesLoading: boolean;
  participationStatusesError: string | null;
  currentPublicSession?: PublicWebinarSession | null;
  publicSessionLoading?: boolean;
  publicSessionError?: string | null;
  /** L'échec ne dit rien de la session (réseau, serveur) : à proposer de réessayer, pas « fermée ». */
  publicSessionErrorTransient?: boolean;
  publicSessionRequestId?: string | null;
  /** Session `webinarId:sessionIdentifier` demandée en dernier, par cette page ou celle du live. */
  publicSessionKey?: string | null;
  currentPublicSubscriptionForm?: PublicWebinarSubscriptionForm | null;
  publicSubscriptionFormLoading?: boolean;
  publicSubscriptionFormError?: string | null;
  currentPublicWebinarIndex?: WebinarIndex | null;
  publicWebinarIndexLoading?: boolean;
  publicWebinarIndexError?: string | null;
  currentPublicWaitingRoomSurvey?: SurveyTemplate | null;
  publicWaitingRoomSurveyLoading?: boolean;
  publicWaitingRoomSurveyError?: string | null;
  currentPublicWaitingRoomQuiz?: QuizTemplate | null;
  publicWaitingRoomQuizLoading?: boolean;
  publicWaitingRoomQuizError?: string | null;
  currentPublicWaitingRoomPage?: WaitingRoomPage | null;
  publicWaitingRoomPageLoading?: boolean;
  publicWaitingRoomPageError?: string | null;
  publicWebinarRegisterLoading?: boolean;
  publicWebinarRegisterError?: string | null;
  publicSurveySubmitLoading?: boolean;
  publicSurveySubmitError?: string | null;
  publicQuizSubmitLoading?: boolean;
  publicQuizSubmitError?: string | null;
  publicWaitingRoomSurveySubmitLoading?: boolean;
  publicWaitingRoomSurveySubmitError?: string | null;
  currentPublicSurveySubmission?: PublicSurveySubmission | null;
  publicSurveySubmissionLoading?: boolean;
  publicSurveySubmissionError?: string | null;
  currentPublicQuizSubmission?: QuizSubmission | null;
  publicQuizSubmissionLoading?: boolean;
  publicQuizSubmissionError?: string | null;
  currentPublicWebinarMe?: PublicWebinarMe | null;
  publicWebinarMeLoading?: boolean;
  publicWebinarMeError?: string | null;
  currentIntegrationsMe?: TechnicalAuthUser | null;
  integrationsMeLoading?: boolean;
  integrationsMeError?: string | null;
  workspaceAccesses?: WorkspaceTeamMember[];
  workspaceAccessesLoading?: boolean;
  workspaceAccessesError?: string | null;
  evaluationTemplate?: EvaluationTemplate | null;
  evaluationTemplateLoading?: boolean;
  evaluationTemplateError?: string | null;
  evaluationQuestions?: EvaluationQuestion[];
  evaluationQuestionsLoading?: boolean;
  evaluationQuestionsError?: string | null;
  createEvaluationQuestionLoading?: boolean;
  createEvaluationQuestionError?: string | null;
  updateEvaluationTemplateLoading?: boolean;
  updateEvaluationTemplateError?: string | null;
  updateEvaluationQuestionLoading?: boolean;
  updateEvaluationQuestionError?: string | null;
  deleteEvaluationQuestionLoading?: boolean;
  deleteEvaluationQuestionError?: string | null;
  webhooks?: WebhookDto[];
  webhooksLoading?: boolean;
  webhooksError?: string | null;
  updateWebhookLoading?: boolean;
  updateWebhookError?: string | null;
  deleteWebhookLoading?: boolean;
  deleteWebhookError?: string | null;
  integrationApiKeys?: IntegrationApiKeyDto[];
  integrationApiKeysLoading?: boolean;
  integrationApiKeysError?: string | null;
  automationTriggers?: WebinarIntegrationTrigger[];
  automationTriggersLoading?: boolean;
  automationTriggersError?: string | null;
  updateAutomationTriggerLoading?: boolean;
  updateAutomationTriggerError?: string | null;
  deleteAutomationTriggerLoading?: boolean;
  deleteAutomationTriggerError?: string | null;
}
